import { useState, useRef } from 'react';
import { Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export default function SwipeableCartItem({ item, onUpdate, onRemove }) {
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const currentX = useRef(0);
  const containerRef = useRef(null);

  const SWIPE_THRESHOLD = 80;
  const DELETE_THRESHOLD = 150;

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    currentX.current = startX.current;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    currentX.current = e.touches[0].clientX;
    const diff = currentX.current - startX.current;
    
    // Only allow left swipe (negative values)
    if (diff < 0) {
      setTranslateX(Math.max(diff, -DELETE_THRESHOLD));
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    
    if (translateX < -DELETE_THRESHOLD * 0.8) {
      // Delete the item
      if (navigator.vibrate) navigator.vibrate(50);
      onRemove(item.id);
    } else if (translateX < -SWIPE_THRESHOLD) {
      // Show delete button
      setTranslateX(-SWIPE_THRESHOLD);
    } else {
      // Reset position
      setTranslateX(0);
    }
  };

  const handleDelete = () => {
    if (navigator.vibrate) navigator.vibrate(50);
    onRemove(item.id);
  };

  return (
    <div className="relative overflow-hidden rounded-xl" ref={containerRef}>
      {/* Delete background */}
      <div 
        className={cn(
          "absolute inset-y-0 right-0 flex items-center justify-end bg-destructive transition-opacity",
          translateX < 0 ? "opacity-100" : "opacity-0"
        )}
        style={{ width: Math.abs(translateX) + 'px' }}
      >
        <button
          onClick={handleDelete}
          className="h-full px-4 flex items-center justify-center text-white"
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </div>

      {/* Main content */}
      <div
        className={cn(
          "flex gap-3 p-3 bg-muted/50 transition-transform",
          !isDragging && "duration-200"
        )}
        style={{ transform: `translateX(${translateX}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-testid={`cart-item-${item.id}`}
      >
        {item.image_url && (
          <img 
            src={item.image_url} 
            alt={item.name}
            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
            draggable={false}
          />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm truncate">{item.name}</h4>
          <p className="text-sm text-muted-foreground price-tag">
            ${item.unit_price.toFixed(2)} each
          </p>
          {item.special_instructions && (
            <p className="text-xs text-muted-foreground mt-1 truncate">
              Note: {item.special_instructions}
            </p>
          )}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full touch-manipulation"
                onClick={() => onUpdate(item.id, item.quantity - 1)}
                data-testid={`decrease-qty-${item.id}`}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <span className="w-8 text-center text-sm font-medium">
                {item.quantity}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full touch-manipulation"
                onClick={() => onUpdate(item.id, item.quantity + 1)}
                data-testid={`increase-qty-${item.id}`}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            <span className="font-semibold text-sm price-tag">
              ${item.total_price.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Swipe hint */}
      {translateX === 0 && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/50 hidden md:hidden">
          ← Swipe to delete
        </div>
      )}
    </div>
  );
}
